/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   saveUserInfo thunk
 */

import { setLocalUser, setUser } from '@zcode-ui/web-zumper-global-state'
import { type RawLocalUser, type TenantProfile, User } from '@zcode/bizos'
import { localStorageSingleton, saveUserToLocalStorage } from '@zcode/nr-utils'
import type { Profile } from '@zcode/state-type'
import type { MaybeEmpty } from '@zcode/utility-types'
import invariant from 'tiny-invariant'
import { logger } from '../../logger'
import * as Api from '../api/index'
import { updateUser } from '../modules/user/thunks'
import type { Dispatch } from '../types'
import Marketing from '../utils/marketing'

interface Data {
  email: string | null | undefined
  firstName: string | undefined
  lastName: string | undefined
  moveIn?: string | null
  moveInText: 'ASAP' | 'In the next 30 days' | 'Later this year' | undefined
  name: string | undefined
  noRetargetLead: boolean
  phone: string | null | undefined
  user: User | null | undefined
}

interface NonMoveInData {
  email: string | null | undefined
  firstName?: string | undefined
  lastName?: string | undefined
  moveIn?: string | null
  moveInText?: never
  name?: string | undefined
  noRetargetLead?: boolean
  phone?: string | null | undefined
  user?: User | null | undefined
}

function saveUserInfo(data: NonMoveInData): (dispatch: Dispatch) => void
function saveUserInfo(
  data: Data,
  setProfile: (profile: Profile) => void
): (dispatch: Dispatch) => void
function saveUserInfo(
  data: Data | NonMoveInData,
  setProfile?: (profile: Profile) => void
): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    const { email, firstName, lastName, moveIn, moveInText, name, noRetargetLead, phone, user } =
      data

    // using snake_case to match User api definition (the input to the User bizo ctor)
    const userData = {
      email: email ?? undefined,
      phone: phone ?? undefined,
      first_name: firstName,
      last_name: lastName,
      long_name: name,
      tenant_profile: {
        move_in: moveIn ?? null,
        move_in_text: moveInText,
      },
    } satisfies RawLocalUser

    if (user) {
      const tenantProfile: MaybeEmpty<TenantProfile> = user.tenantProfile || {}
      const shouldUpdateMoveIn = moveIn && moveIn !== tenantProfile.moveIn
      const shouldUpdatePhone = phone && phone !== user.phone
      const userCopy = { ...user.toJSON() }
      if (phone) {
        if (!noRetargetLead) {
          // @ts-expect-error missing properties moveIn, joinedAt, ...
          Marketing.trackIdentify({ email, firstName, lastName, phone })
        }
        if (!user.phone) {
          userCopy.phone = phone
        }
      }
      if (shouldUpdateMoveIn) {
        userCopy.tenant_profile = userCopy.tenant_profile
          ? { ...userCopy.tenant_profile, move_in: moveIn }
          : { move_in: moveIn }
      }
      if (shouldUpdateMoveIn || shouldUpdatePhone) {
        // @ts-expect-error phone_validation_mode doesn't exist on RawUser
        userCopy.phone_validation_mode = 'hack'
        void dispatch(updateUser(userCopy))
        dispatch(setUser({ user: User.fromJS(userCopy) }))
      }
      if (shouldUpdateMoveIn && moveInText) {
        invariant(setProfile)
        // 🤔 Should we allow unsetting moveInText too?
        void saveMoveInText(moveInText, setProfile)
      }
    } else {
      // update localStorage user if user has entered new email or name
      const localUser = new User(localStorageSingleton.get('user') || {})
      // TODO: make a util to compare local user with tenant user
      if (
        localUser.email !== email ||
        localUser.longName !== userData.long_name ||
        localUser.phone !== phone ||
        (localUser.tenantProfile && localUser.tenantProfile.moveIn !== moveIn)
      ) {
        saveUserToLocalStorage(userData)
        // set user
        dispatch(setLocalUser(new User(userData)))
      }
    }
  }
}

async function saveMoveInText(
  moveInText: Exclude<Data['moveInText'], undefined>,
  setProfile: (profile: Profile) => void
) {
  const value = (
    {
      ASAP: 'ASAP',
      'In the next 30 days': 'WITHIN_A_MONTH',
      'Later this year': 'LATER_THIS_YEAR',
    } as const
  )[moveInText]
  if (!value) {
    logger.error('Unrecognized moveInText value', moveInText)
    return
  }

  const profile = { explicit: { MOVE_IN: { source: 'WEB_MESSAGE_FORM', value } } }
  try {
    setProfile(await Api.profileCreate.post(profile))
  } catch (e: unknown) {
    if (typeof e === 'object' && e && 'status' in e && e.status === 409) {
      setProfile(await Api.profile.put(profile))
    } else {
      logger.error('failed to save profile moveInText')
    }
  }
}

export default saveUserInfo
